/**
 * Platform admin card: reconcile chat usage counters with Stripe billing
 * periods (POST /api/user-profile/admin/recover-usage-counters).
 */
import React, { useState } from 'react';
import { ActivityIndicator, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { Button } from '@/components/ui/button';
import { Text } from '@/components/ui/text';
import { AdminCalloutBanner, AdminSectionCard, ADMIN_COLORS } from '@/components/admin/AdminUI';
import { apiRequest } from '../../services/apiClient';
import { useTranslation } from '../../hooks/useTranslation';
import { confirmDialog, showDialog } from '../../utils/dialogs';

interface UsageRecoveryResult {
  message: string;
  corrections: {
    user_id: number;
    email: string;
    old_chat_requests_this_month: number;
    new_chat_requests_this_month: number;
    old_last_monthly_reset: string;
    new_last_monthly_reset: string;
  }[];
}

export default function UsageCounterRecoveryCard() {
  const { t } = useTranslation();
  const [usageRecoveryLoading, setUsageRecoveryLoading] = useState(false);
  const [usageRecoveryResult, setUsageRecoveryResult] = useState<UsageRecoveryResult | null>(null);

  const handleRecoverUsageCounters = async () => {
    const confirmed = await confirmDialog(
      t('platformAdmin.recoverUsageConfirmTitle'),
      t('platformAdmin.recoverUsageConfirm'),
      t('platformAdmin.recoverUsageAction'),
      t('common.cancel'),
    );
    if (!confirmed) return;
    try {
      setUsageRecoveryLoading(true);
      setUsageRecoveryResult(null);
      const result = await apiRequest<UsageRecoveryResult>({
        endpoint: '/user-profile/admin/recover-usage-counters',
        method: 'POST',
      });
      setUsageRecoveryResult(result);
    } catch (error: any) {
      const message = error?.message || 'Failed to recover usage counters';
      showDialog(message);
    } finally {
      setUsageRecoveryLoading(false);
    }
  };

  return (
    <AdminSectionCard
      icon="sync"
      title="Usage Counter Recovery"
      subtitle="Reconcile chat usage counters to align with Stripe billing periods instead of calendar months."
    >
      <Button
        variant="outline"
        disabled={usageRecoveryLoading}
        onPress={handleRecoverUsageCounters}
        className="flex-row items-center justify-center gap-2"
      >
        {usageRecoveryLoading ? (
          <ActivityIndicator size="small" color={ADMIN_COLORS.text.primary} />
        ) : (
          <MaterialIcons name="sync" size={18} color={ADMIN_COLORS.text.primary} />
        )}
        <Text className="text-sm font-semibold" style={{ color: ADMIN_COLORS.text.primary }}>
          Recover Usage Counters
        </Text>
      </Button>

      {usageRecoveryResult ? (
        <View
          style={{
            backgroundColor: ADMIN_COLORS.surface,
            borderWidth: 1,
            borderColor: ADMIN_COLORS.borderSoft,
            borderRadius: 14,
            padding: 14,
            gap: 12,
          }}
        >
          <Text className="text-sm" style={{ color: ADMIN_COLORS.text.primary }}>
            {usageRecoveryResult.message}
          </Text>
          {usageRecoveryResult.corrections.length > 0 ? (
            <View style={{ gap: 6 }}>
              <Text
                className="text-xs font-semibold uppercase"
                style={{ color: ADMIN_COLORS.text.muted, letterSpacing: 0.5 }}
              >
                Corrections applied
              </Text>
              {usageRecoveryResult.corrections.map((item, idx) => (
                <View
                  key={idx}
                  className="flex-row items-start"
                  style={{
                    gap: 10,
                    paddingVertical: 8,
                    borderTopWidth: idx === 0 ? 0 : 1,
                    borderTopColor: ADMIN_COLORS.borderSoft,
                  }}
                >
                  <MaterialIcons name="person" size={16} color={ADMIN_COLORS.success} />
                  <View style={{ flex: 1 }}>
                    <Text className="text-sm" style={{ color: ADMIN_COLORS.text.primary }}>
                      {item.email}
                    </Text>
                    <Text className="text-xs" style={{ color: ADMIN_COLORS.text.secondary }}>
                      Chats: {item.old_chat_requests_this_month} → {item.new_chat_requests_this_month} · Period: {item.new_last_monthly_reset}
                    </Text>
                  </View>
                </View>
              ))}
            </View>
          ) : (
            <AdminCalloutBanner
              tone="success"
              body="All usage counters are already correct."
            />
          )}
        </View>
      ) : null}
    </AdminSectionCard>
  );
}
